import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Eye, Trash2, Plus } from "lucide-react";
import { DiplomaFieldEditor } from "./components/DiplomaFieldEditor";
import { DiplomaReferencePrompt } from "./components/DiplomaReferencePrompt";
import StickySaveBar from "@/components/admin/StickySaveBar";

export default function AdminDiplomas() {
  const { t } = useI18n();
  const [templates, setTemplates] = useState([]);
  const [games, setGames] = useState([]);
  const [active, setActive] = useState(null);
  const [available, setAvailable] = useState(true);
  const [filterGame, setFilterGame] = useState("__all__");
  const [filterLevel, setFilterLevel] = useState("__all__");
  const [filterTier, setFilterTier] = useState("__all__"); // escalão id for tiered games
  // 2026-02: which language's text is being edited on the Valor/Template
  // inputs. This is independent from `active.language` (the template row's
  // default) so a single Type-6 template can carry different texts per
  // language without needing separate row entries.
  const [editLang, setEditLang] = useState("pt");

  const load = async () => {
    const [tplRes, gamesRes] = await Promise.all([
      api.get("/diplomas/templates"),
      api.get("/games"),
    ]);
    const data = tplRes.data;
    setGames(gamesRes.data || []);
    setTemplates(data);
    if (data.length) {
      // Preserve active selection if possible
      const cur = active ? data.find((d) => d.id === active.id) : null;
      setActive(cur || data[0]);
    }
    const av = await api.get("/diplomas/availability");
    setAvailable(av.data.available);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const clone = (v) => JSON.parse(JSON.stringify(v));
  const [refQueue, setRefQueue] = useState(null); // { items, index, answers, saved }

  const persist = (tpl, extra = {}) => api.put(`/diplomas/templates/${tpl.id}`, {
    name: tpl.name,
    language: tpl.language,
    game_id: tpl.game_id || null,
    level: tpl.level ?? null,
    tier_id: tpl.tier_id ?? null,
    background_url: tpl.background_url,
    fields: tpl.fields,
    active: tpl.active,
    use_reference: tpl.type_number === 1 ? true : tpl.use_reference !== false,
    criteria_type: tpl.criteria_type || "none",
    criteria_rank_min: tpl.criteria_rank_min ?? 1,
    criteria_rank_max: tpl.criteria_rank_max ?? 9999,
    criteria_time_min: tpl.criteria_time_min ?? 0,
    criteria_time_max: tpl.criteria_time_max ?? 99999,
    ...extra,
  });

  // Diff Type 1 fields against the last saved version → one question per changed field.
  const diffReferenceChanges = (savedFields, newFields) => {
    if ((savedFields || []).length !== (newFields || []).length) return [{ kind: "structure" }];
    const items = [];
    newFields.forEach((f, i) => {
      if (JSON.stringify(f) !== JSON.stringify(savedFields[i])) {
        items.push({ kind: "field", index: i, label: f.name || `campo_${i + 1}`, oldName: savedFields[i]?.name });
      }
    });
    return items;
  };

  const save = async () => {
    if (!active) return;
    const savedTpl = templates.find((t) => t.id === active.id);
    await persist(active);
    toast.success("Guardado");
    if (active.type_number === 1 && savedTpl) {
      const items = diffReferenceChanges(savedTpl.fields, active.fields);
      const targets = templates.filter((t) => t.type_number !== 1 && t.use_reference !== false);
      if (items.length && targets.length) {
        setRefQueue({ items, index: 0, answers: [], newFields: clone(active.fields), targets });
        return;
      }
    }
    load();
  };

  // Applies the "Sim" answers to every diploma 2-6 with the reference toggle ON.
  const applyReferenceAnswers = async (q) => {
    const yes = q.items.filter((_, i) => q.answers[i]);
    if (yes.length) {
      await Promise.all(q.targets.map((tgt) => {
        let fields = clone(tgt.fields || []);
        yes.forEach((it) => {
          if (it.kind === "structure") { fields = clone(q.newFields); return; }
          const nf = clone(q.newFields[it.index]);
          let idx = fields.findIndex((f) => f.name === it.oldName);
          if (idx < 0 && it.index < fields.length) idx = it.index;
          if (idx >= 0) fields[idx] = nf; else fields.push(nf);
        });
        return api.put(`/diplomas/templates/${tgt.id}`, { fields });
      }));
      toast.success(`Alterações aplicadas a ${q.targets.length} diploma(s)`);
    }
    setRefQueue(null);
    load();
  };

  const answerReference = (yes) => {
    const q = refQueue;
    const answers = [...q.answers, yes];
    if (answers.length >= q.items.length) { applyReferenceAnswers({ ...q, answers }); return; }
    setRefQueue({ ...q, answers, index: q.index + 1 });
  };

  // Tipos 2-6: ligar copia os campos do Tipo 1 (apaga os atuais); desligar mantém os campos.
  const toggleReference = (on) => {
    if (!on) { setActive({ ...active, use_reference: false }); return; }
    const ref = templates.find((t) => t.type_number === 1);
    if (!ref) { toast.error("Diploma Tipo 1 não encontrado"); return; }
    setActive({ ...active, use_reference: true, fields: clone(ref.fields || []) });
    toast.info("Campos repostos a partir do Tipo 1 — guarda para confirmar");
  };

  // Functional updaters with stable identity so each memoised field block only
  // re-renders when its own data changes.
  const patchFields = (prev, i, patch) => ({
    ...prev,
    fields: prev.fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)),
  });
  const updateField = useCallback((i, patch) => setActive((prev) => patchFields(prev, i, patch)), []);

  // Keeps `value` as the PT canonical (legacy rendering) and every language in `values`.
  const updateFieldValue = useCallback((i, lang, newText) => {
    setActive((prev) => {
      const cur = prev.fields[i] || {};
      const values = { ...(cur.values || {}), [lang]: newText };
      const patch = { values };
      if (lang === "pt") patch.value = newText;
      return patchFields(prev, i, patch);
    });
  }, []);

  const addField = () => {
    const arr = [...(active.fields || [])];
    arr.push({
      name: `campo_${arr.length + 1}`,
      value: "", values: { pt: "", en: "", es: "" },
      x: 0, y: 400, nudge_x: 0, nudge_y: 0,
      font: "Helvetica", color: "#334155", size: 16,
      bold: false, italic: false, active: true, align: "center",
    });
    setActive({ ...active, fields: arr });
  };

  const removeField = useCallback((i) => {
    setActive((prev) => ({ ...prev, fields: prev.fields.filter((_, idx) => idx !== i) }));
  }, []);

  const toggleAvailable = async (v) => {
    setAvailable(v);
    await api.put("/diplomas/availability", { available: v });
    toast.success(v ? "Diplomas disponíveis" : "Diplomas indisponíveis");
  };

  const onUploadBg = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagem demasiado grande (máx 5 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setActive({ ...active, background_url: ev.target.result });
    reader.readAsDataURL(file);
  };

  const preview = async () => {
    if (!active) return;
    try {
      // Save first so the preview uses the most up-to-date data
      await save();
      const res = await api.get(`/diplomas/templates/${active.id}/preview`, {
        params: { lang: editLang },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar pré-visualização");
    }
  };

  const ct = active?.criteria_type || "none";

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className="text-3xl font-[Outfit] font-semibold text-slate-900">{t("diplomas")}</h1>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2">
          <Switch checked={available} onCheckedChange={toggleAvailable} data-testid="diploma-availability-switch" />
          <span className="text-sm font-medium">{available ? "Disponível" : "Indisponível"}</span>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="diplomas-filter-bar">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500 px-2">Filtros automáticos no ranking</span>
        <Select value={filterGame} onValueChange={(v) => { setFilterGame(v); setFilterLevel("__all__"); setFilterTier("__all__"); }}>
          <SelectTrigger className="max-w-[260px]" data-testid="diploma-filter-game"><SelectValue placeholder="Jogo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos os jogos</SelectItem>
            {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select
          value={(() => {
            const g = games.find((x) => x.id === filterGame);
            const useT = g?.level_system === "tiers" && Array.isArray(g?.tiers);
            return useT ? filterTier : filterLevel;
          })()}
          onValueChange={(v) => {
            const g = games.find((x) => x.id === filterGame);
            const useT = g?.level_system === "tiers" && Array.isArray(g?.tiers);
            if (useT) { setFilterTier(v); setFilterLevel("__all__"); }
            else { setFilterLevel(v); setFilterTier("__all__"); }
          }}
        >
          <SelectTrigger className="max-w-[200px]" data-testid="diploma-filter-level"><SelectValue placeholder="Nível" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{(() => {
              const g = games.find((x) => x.id === filterGame);
              return g?.level_system === "tiers" ? "Todos os escalões" : "Todos os níveis";
            })()}</SelectItem>
            {(() => {
              const g = games.find((x) => x.id === filterGame);
              if (g?.level_system === "tiers" && Array.isArray(g.tiers) && g.tiers.length > 0) {
                return g.tiers.map((tr) => (
                  <SelectItem key={tr.id} value={tr.id}>{tr.name} <span className="text-slate-400 text-xs">(Nv {tr.level})</span></SelectItem>
                ));
              }
              return [1, 2, 3, 4, 5].map((lv) => (
                <SelectItem key={lv} value={String(lv)}>Nível {lv}</SelectItem>
              ));
            })()}
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={() => { setFilterGame("__all__"); setFilterLevel("__all__"); setFilterTier("__all__"); }}
          className="text-xs text-sky-700 hover:text-sky-900 underline font-bold"
          data-testid="diploma-filter-reset"
        >
          Repor
        </button>
        <div className="ml-auto text-xs text-slate-500 pr-2">
          {(() => {
            const lv = filterLevel === "__all__" ? null : parseInt(filterLevel, 10);
            const tid = filterTier === "__all__" ? null : filterTier;
            const gid = filterGame === "__all__" ? null : filterGame;
            const n = templates.filter((t2) => {
              if (gid && t2.game_id && t2.game_id !== gid) return false;
              // Hide only templates explicitly scoped to a DIFFERENT escalão;
              // generic (no tier_id) templates still apply and stay visible.
              if (tid != null) { if (t2.tier_id && t2.tier_id !== tid) return false; }
              else if (lv != null && t2.level != null && t2.level !== lv) return false;
              return true;
            }).length;
            return `${n} de ${templates.length} templates visíveis`;
          })()}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-3 space-y-2">
          {templates
            .filter((tpl) => {
              const lv = filterLevel === "__all__" ? null : parseInt(filterLevel, 10);
              const tid = filterTier === "__all__" ? null : filterTier;
              const gid = filterGame === "__all__" ? null : filterGame;
              if (gid && tpl.game_id && tpl.game_id !== gid) return false;
              if (tid != null) { if (tpl.tier_id && tpl.tier_id !== tid) return false; }
              else if (lv != null && tpl.level != null && tpl.level !== lv) return false;
              return true;
            })
            .map((tpl) => {
              const scope = [];
              if (tpl.game_id) {
                const g = games.find((x) => x.id === tpl.game_id);
                scope.push(g ? g.name : "Jogo");
              }
              if (tpl.tier_id || tpl.level != null) {
                const g = games.find((x) => x.id === tpl.game_id);
                const tiers = g?.level_system === "tiers" && Array.isArray(g.tiers) ? g.tiers : null;
                const tname = tiers
                  ? (tiers.find((tr) => tr.id === tpl.tier_id)?.name
                     || tiers.find((tr) => Number(tr.level) === Number(tpl.level))?.name)
                  : null;
                scope.push(tname || `Nv ${tpl.level}`);
              }
              return (
                <button
                  key={tpl.id}
                  onClick={() => setActive(tpl)}
                  data-testid={`tpl-tab-${tpl.type_number}`}
                  className={`w-full text-left px-4 py-3 rounded-lg border ${active?.id === tpl.id ? "bg-sky-50 border-sky-300" : "bg-white border-slate-200"}`}
                >
                  <div className="font-bold text-sm">Tipo {tpl.type_number}</div>
                  <div className="text-xs text-slate-500">{tpl.name}</div>
                  {scope.length > 0 && (
                    <div className="text-[10px] text-sky-700 mt-1 font-medium">{scope.join(" · ")}</div>
                  )}
                </button>
              );
            })}
        </div>

        {refQueue && <DiplomaReferencePrompt queue={refQueue.items} index={refQueue.index} onAnswer={answerReference} />}
        {active && (
          <div className="col-span-12 md:col-span-9 bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
            {active.type_number !== 1 && (
              <div className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 ${active.use_reference !== false ? "bg-sky-50 border-sky-200" : "bg-slate-50 border-slate-200"}`} data-testid="tpl-reference-box">
                <div className="text-sm">
                  <div className="font-bold text-slate-800">Definições de referência (Tipo 1)</div>
                  <div className="text-xs text-slate-500">
                    {active.use_reference !== false
                      ? "Ligado: este diploma assumiu os campos (textos, posições, fontes, tamanhos) do Tipo 1. Podes alterá-los e guardar. Ao desligar e voltar a ligar, os campos do Tipo 1 são copiados de novo, apagando os atuais."
                      : "Desligado: campos próprios. Liga para copiar os campos do Tipo 1 (apaga os atuais)."}
                  </div>
                </div>
                <Switch checked={active.use_reference !== false} onCheckedChange={toggleReference} data-testid="tpl-reference-toggle" />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label>Nome</Label><Input value={active.name} onChange={(e) => setActive({ ...active, name: e.target.value })} data-testid="tpl-name" /></div>
              <div>
                <Label>Idioma</Label>
                <Select value={active.language} onValueChange={(v) => setActive({ ...active, language: v })}>
                  <SelectTrigger data-testid="tpl-language"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PT">PT</SelectItem>
                    <SelectItem value="EN">EN</SelectItem>
                    <SelectItem value="ES">ES</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 mt-6">
                <Switch checked={active.active} onCheckedChange={(v) => setActive({ ...active, active: v })} data-testid="tpl-active" />
                <Label>Ativo</Label>
              </div>
            </div>

            {/* Scope: which game/level this diploma applies to */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Jogo a que se refere</Label>
                <Select
                  value={active.game_id || "__any__"}
                  onValueChange={(v) => setActive({ ...active, game_id: v === "__any__" ? null : v, tier_id: null, level: null })}
                >
                  <SelectTrigger data-testid="tpl-game"><SelectValue placeholder="Qualquer jogo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any__">Qualquer jogo</SelectItem>
                    {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-slate-500 mt-1">Se vazio, aplica-se a todos os jogos.</div>
              </div>
              <div>
                <Label className="text-xs">{(() => {
                  const g = games.find((x) => x.id === active.game_id);
                  return g?.level_system === "tiers" ? "Nível/Escalão a que se refere" : "Nível a que se refere";
                })()}</Label>
                <Select
                  value={(() => {
                    const g = games.find((x) => x.id === active.game_id);
                    const useT = g?.level_system === "tiers" && Array.isArray(g?.tiers);
                    if (useT) return active.tier_id || "__any__";
                    return active.level == null ? "__any__" : String(active.level);
                  })()}
                  onValueChange={(v) => {
                    const g = games.find((x) => x.id === active.game_id);
                    const useT = g?.level_system === "tiers" && Array.isArray(g?.tiers);
                    if (v === "__any__") { setActive({ ...active, tier_id: null, level: null }); return; }
                    if (useT) {
                      const tr = g.tiers.find((t) => t.id === v);
                      // Store the escalão id (precise) and keep the numeric level
                      // in sync for display / legacy fallback.
                      setActive({ ...active, tier_id: tr ? tr.id : null, level: tr && tr.level != null ? parseInt(tr.level, 10) : null });
                    } else {
                      setActive({ ...active, level: parseInt(v, 10), tier_id: null });
                    }
                  }}
                >
                  <SelectTrigger data-testid="tpl-level"><SelectValue placeholder="Qualquer nível" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any__">{(() => {
                      const g = games.find((x) => x.id === active.game_id);
                      return g?.level_system === "tiers" ? "Qualquer escalão" : "Qualquer nível";
                    })()}</SelectItem>
                    {(() => {
                      const g = games.find((x) => x.id === active.game_id);
                      if (g?.level_system === "tiers" && Array.isArray(g.tiers) && g.tiers.length > 0) {
                        return g.tiers.map((tr) => (
                          <SelectItem key={tr.id} value={tr.id}>{tr.name} <span className="text-slate-400 text-xs">(Nv {tr.level})</span></SelectItem>
                        ));
                      }
                      return [1, 2, 3, 4, 5].map((lv) => <SelectItem key={lv} value={String(lv)}>Nível {lv}</SelectItem>);
                    })()}
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-slate-500 mt-1">Se vazio, aplica-se a todos.</div>
              </div>
            </div>

            {/* Background image upload */}
            <div>
              <Label className="mb-2 block">Imagem de fundo</Label>
              <div className="flex items-start gap-3">
                {active.background_url ? (
                  <div className="relative">
                    <img src={active.background_url} alt="bg" className="w-32 h-20 object-contain bg-slate-100 rounded border border-slate-200" />
                    <button
                      type="button"
                      onClick={() => setActive({ ...active, background_url: "" })}
                      className="absolute -top-2 -right-2 bg-rose-500 text-white rounded-full p-1 hover:bg-rose-600"
                      data-testid="tpl-bg-clear"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-20 bg-slate-100 rounded border border-dashed border-slate-300 grid place-items-center text-slate-400 text-xs">Sem imagem</div>
                )}
                <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 bg-sky-100 text-sky-700 rounded border border-sky-300 hover:bg-sky-200">
                  <Upload className="w-4 h-4" /> Carregar (máx 5 MB)
                  <input type="file" accept="image/*" className="hidden" onChange={onUploadBg} data-testid="tpl-bg-upload" />
                </label>
              </div>
            </div>

            {/* Criteria block */}
            <div className="border-t border-slate-200 pt-4">
              <div className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">Critério de atribuição</div>
              <p className="text-xs text-slate-500 mb-3">Define quando este tipo de diploma é atribuído a um utilizador.</p>
              <div className="grid md:grid-cols-3 gap-3 items-end">
                <div>
                  <Label>Tipo de critério</Label>
                  <Select value={ct} onValueChange={(v) => setActive({ ...active, criteria_type: v })}>
                    <SelectTrigger data-testid="tpl-criteria-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rank">Por ranking (posição)</SelectItem>
                      <SelectItem value="time">Por melhor tempo (segundos)</SelectItem>
                      <SelectItem value="none">Sem critério (sempre)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {ct === "rank" && (
                  <>
                    <div>
                      <Label>Posição mínima</Label>
                      <Input type="number" min={1} value={active.criteria_rank_min ?? 1} onChange={(e) => setActive({ ...active, criteria_rank_min: parseInt(e.target.value || "1", 10) })} data-testid="tpl-rank-min" />
                    </div>
                    <div>
                      <Label>Posição máxima</Label>
                      <Input type="number" min={1} value={active.criteria_rank_max ?? 9999} onChange={(e) => setActive({ ...active, criteria_rank_max: parseInt(e.target.value || "9999", 10) })} data-testid="tpl-rank-max" />
                    </div>
                  </>
                )}
                {ct === "time" && (
                  <>
                    <div>
                      <Label>Tempo mínimo (s)</Label>
                      <Input type="number" step="0.1" min={0} value={active.criteria_time_min ?? 0} onChange={(e) => setActive({ ...active, criteria_time_min: parseFloat(e.target.value || "0") })} data-testid="tpl-time-min" />
                    </div>
                    <div>
                      <Label>Tempo máximo (s)</Label>
                      <Input type="number" step="0.1" min={0} value={active.criteria_time_max ?? 99999} onChange={(e) => setActive({ ...active, criteria_time_max: parseFloat(e.target.value || "99999") })} data-testid="tpl-time-max" />
                    </div>
                  </>
                )}
              </div>
              {ct === "rank" && <p className="text-[11px] text-slate-500 mt-2">Ex.: {active.criteria_rank_min ?? 1} a {active.criteria_rank_max ?? 9999} → os jogadores nessas posições do ranking recebem este diploma.</p>}
              {ct === "time" && <p className="text-[11px] text-slate-500 mt-2">Ex.: {active.criteria_time_min ?? 0}s a {active.criteria_time_max ?? 99999}s → jogadores com melhor tempo nesse intervalo recebem este diploma.</p>}
            </div>

            <div>
              <Label className="mb-2 block">Campos a desenhar no PDF</Label>
              <div className="flex items-center gap-2 mb-2 bg-sky-50 border border-sky-200 rounded-lg p-2" data-testid="tpl-edit-lang-switcher">
                <span className="text-xs font-bold text-sky-800">Editar textos em:</span>
                {["pt", "en", "es"].map((lg) => (
                  <button
                    key={lg}
                    type="button"
                    onClick={() => setEditLang(lg)}
                    data-testid={`tpl-edit-lang-${lg}`}
                    className={`text-xs font-bold px-2.5 py-1 rounded-full border transition ${
                      editLang === lg
                        ? "bg-sky-600 text-white border-sky-600"
                        : "bg-white text-sky-700 border-sky-200 hover:bg-sky-100"
                    }`}
                  >
                    {lg.toUpperCase()}
                  </button>
                ))}
                <span className="ml-auto text-[11px] text-slate-500 italic">
                  Cada idioma guarda o seu próprio texto — as posições / cor / fonte são partilhadas.
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-2">
                Para cada campo, ajusta o <b>deslocamento</b> em relação à posição predefinida (0 = posição base).
                <br />
                <span className="font-semibold">Subir/Direita</span>: valores positivos (0 a 100). <span className="font-semibold">Descer/Esquerda</span>: valores negativos (-100 a 0).
              </p>
              <div data-testid="tpl-placeholders-hint" className="text-[11px] bg-amber-50 border border-amber-200 rounded p-2 mb-3 text-amber-900">
                <b>Variáveis disponíveis</b> (usa entre chavetas no campo Valor):{" "}
                <code className="bg-white px-1 rounded">{"{name}"}</code>{" "}
                <code className="bg-white px-1 rounded">{"{school}"}</code>{" "}
                <code className="bg-white px-1 rounded">{"{competition}"}</code>{" "}
                <code className="bg-white px-1 rounded">{"{time}"}</code>{" "}
                <code className="bg-white px-1 rounded">{"{ranking}"}</code>{" "}
                <code className="bg-white px-1 rounded">{"{level}"}</code>{" "}
                <code className="bg-white px-1 rounded">{"{tier}"}</code>{" "}
                <code className="bg-white px-1 rounded">{"{total_participants}"}</code>{" "}
                <code className="bg-white px-1 rounded">{"{avg_best_time}"}</code>{" "}
                <code className="bg-white px-1 rounded">{"{avg_time}"}</code>
                <br />
                <span className="opacity-80">
                  No campo <i>motivador</i> podes usar <b>Enter</b> para criar texto com várias linhas (2, 3 ou 4 linhas).
                </span>
              </div>
              <div className="space-y-3 max-h-[600px] overflow-auto pr-2">
                {active.fields.map((f, i) => (
                  <DiplomaFieldEditor
                    key={`f-${i}`}
                    f={f}
                    i={i}
                    editLang={editLang}
                    onPatch={updateField}
                    onValue={updateFieldValue}
                    onRemove={removeField}
                  />
                ))}
                <button
                  type="button"
                  data-testid="tpl-field-add"
                  onClick={addField}
                  className="w-full py-2 rounded-lg border-2 border-dashed border-slate-300 text-slate-600 hover:border-sky-400 hover:text-sky-700 hover:bg-sky-50 text-sm font-medium flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Adicionar campo
                </button>
              </div>
            </div>

            <StickySaveBar align="end">
              <Button data-testid="btn-preview-tpl" onClick={preview} variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50">
                <Eye className="w-4 h-4 mr-2" /> Pré-visualizar
              </Button>
              <Button data-testid="btn-save-tpl" onClick={save} className="bg-slate-900">{t("save")}</Button>
            </StickySaveBar>
          </div>
        )}
      </div>
    </div>
  );
}
